import React, { useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { PermissionName } from "@mapmycustomers/shared";
import { Company, Deal, EntityType, Person } from "@mapmycustomers/shared/types/entity";
import useBoolean from "@mapmycustomers/shared/util/hook/useBoolean";
import useDynamicCallback from "@mapmycustomers/shared/util/hook/useDynamicCallback";
import { Button, Modal } from "@mapmycustomers/ui";

import noRecordsImageMap from "@app/component/associations/utils/noRecordsImages";
import CreateDealModal from "@app/component/createEditEntity/CreateDealModal";
import { hasPermission } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import { useRecordLimitCheckWith } from "@app/util/hook/useRecordLimitCheck";

import Associations from "../components/Associations";
import { filterDeals, getDeals, getFilteredDealsCount, getTotalDeals, isLoading } from "../store";
import useEntitySorter from "../utils/useEntitySorter";

import styles from "./DealAssociation.module.scss";
import messages from "./messages";

interface DealAssociationProps {
  allowAdd?: boolean;
  associatedDeals?: Deal[];
  associateWith?: Deal["id"];
  defaultQuery?: string;
  disableNonEditableEntities?: boolean;
  fixedCompany?: Company;
  fixedPerson?: Person;
  multiselect?: boolean;
  // Overrides the footer's Add/Update wording for a caller that only chooses a record and writes
  // nothing. CompanyAssociation and PeopleAssociation already take the same prop.
  okText?: string;
  onDealCreated?: (newDeal: Deal) => void;
  onHide: (updated?: boolean) => void;
  onSelect?: (selectedDealsIds: Deal["id"][], removedDealsIds?: Deal["id"][]) => void;
  suggestedDeals?: Deal[];
  title?: string;
  // Opened from inside another layer — a Field Agent chat row — this modal must be told to sit
  // above it. CompanyAssociation and PeopleAssociation already take the same prop.
  zIndex?: number;
}

const DealAssociation = ({
  allowAdd = true,
  associatedDeals,
  associateWith,
  canCreateDeals,
  deals,
  defaultQuery = "",
  disableNonEditableEntities,
  filtered,
  fixedCompany,
  fixedPerson,
  loading,
  multiselect = true,
  okText,
  onDealCreated,
  onHide,
  onSearch,
  onSelect,
  suggestedDeals,
  title,
  total,
  zIndex,
}: DealAssociationProps & typeof mapDispatchToProps & ReturnType<typeof mapStateToProps>) => {
  const intl = useIntl();
  const assignedDeals = useMemo(() => {
    return associatedDeals
      ? associatedDeals
      : multiselect
        ? (deals?.filter(
            (deal) =>
              (!!fixedCompany && deal.account?.id === fixedCompany.id) ||
              (!!fixedPerson && deal.contact?.id === fixedPerson.id)
          ) ?? [])
        : [];
  }, [associatedDeals, deals, fixedCompany, fixedPerson, multiselect]);

  const [query, setQuery] = useState<string>(defaultQuery);
  const [newSelectedDealIds, setNewSelectedDealIds] = useState<Set<Deal["id"]>>(() => new Set());
  const [removedDealIds, setRemovedDealIds] = useState<Set<Deal["id"]>>(() => new Set());

  const [createdDeals, setCreatedDeals] = useState<Deal[]>([]);
  const sortedDeals = useEntitySorter(
    deals ?? [],
    createdDeals,
    query.trim().length > 0,
    assignedDeals,
    suggestedDeals
  );

  const assignedDealIds = useMemo(
    () => new Set(assignedDeals.map(({ id }) => id)),
    [assignedDeals]
  );

  const isDealSelected = useCallback(
    (id: Deal["id"]) => {
      const wasInitiallySelectedAndNotRemoved = assignedDealIds.has(id) && !removedDealIds.has(id);

      const wasNewlySelected = newSelectedDealIds.has(id);

      return wasInitiallySelectedAndNotRemoved || wasNewlySelected;
    },
    [assignedDealIds, newSelectedDealIds, removedDealIds]
  );

  const visibleDealIds = useMemo(() => sortedDeals.map(({ id }) => id), [sortedDeals]);

  const selectedDealIds = useMemo(
    () => visibleDealIds.filter((id) => isDealSelected(id)),
    [isDealSelected, visibleDealIds]
  );

  useEffect(() => {
    onSearch({ query: defaultQuery });
  }, [defaultQuery, onSearch]);

  const handleSearch = useCallback((query: string) => onSearch({ query }), [onSearch]);

  const [createNewDealVisible, showCreateDealModal, hideCreateDealModal] = useBoolean();
  const handleHideCreateDealModal = useDynamicCallback((newDeal?: Deal) => {
    const alreadyAssignedDealsIds = new Set(
      [...assignedDeals, ...createdDeals].map(({ id }) => id)
    );
    const hadAnySelectedDealsAlready = selectedDealIds.some(
      (id) => !alreadyAssignedDealsIds.has(id)
    );
    if (newDeal) {
      if (!fixedCompany && !fixedPerson && !onDealCreated) {
        setNewSelectedDealIds((ids) => new Set(multiselect ? [newDeal.id, ...ids] : [newDeal.id]));
      }

      // we also store created deals, just to show then in the top of the list, to not confuse user
      // because otherwise, newly added record could get filtered out according to current filter
      // that's also the reason why we reset the filter here
      setCreatedDeals((deals) => [...deals, newDeal]);
      setQuery("");
      onSearch({ query: "" });
      onDealCreated?.(newDeal);
    }
    hideCreateDealModal();

    // if user selected no deals in the modal before adding new deal, then just hide both modals
    if (!hadAnySelectedDealsAlready && newDeal) {
      onSelect?.([newDeal.id], Array.from(removedDealIds));
      onHide(true);
    }
  });

  const hasChanges = multiselect
    ? newSelectedDealIds.size > 0 || removedDealIds.size > 0
    : newSelectedDealIds.size > 0;

  const alreadyAssignedEntities = useMemo(
    () => [...assignedDeals, ...createdDeals],
    [assignedDeals, createdDeals]
  );

  const handleRowsChange = useCallback(
    (selectedRows: Deal["id"][]) => {
      const currentlySelected = new Set(selectedRows);

      const updatedNewSelectedDealIds = new Set(newSelectedDealIds);
      const updatedRemovedDealIds = new Set(removedDealIds);

      for (const id of visibleDealIds) {
        const wasInitiallyAssigned = assignedDealIds.has(id);
        const isNowSelected = currentlySelected.has(id);

        if (isNowSelected) {
          if (wasInitiallyAssigned) {
            updatedRemovedDealIds.delete(id);
          } else {
            updatedNewSelectedDealIds.add(id);
          }
        } else {
          if (wasInitiallyAssigned) {
            updatedRemovedDealIds.add(id);
          } else {
            updatedNewSelectedDealIds.delete(id);
          }
        }
      }

      setNewSelectedDealIds(updatedNewSelectedDealIds);
      setRemovedDealIds(updatedRemovedDealIds);
    },
    [assignedDealIds, visibleDealIds, newSelectedDealIds, removedDealIds]
  );

  const modalSubTitle = useMemo(() => {
    const subTitle = [];

    if (newSelectedDealIds.size > 0) {
      subTitle.push(
        intl.formatMessage(messages.selectedCount, {
          count: newSelectedDealIds.size,
        })
      );
    }
    if (removedDealIds.size > 0) {
      subTitle.push(
        intl.formatMessage(messages.deselectCount, {
          count: removedDealIds.size,
        })
      );
    }

    return subTitle.join(" • ");
  }, [intl, newSelectedDealIds.size, removedDealIds.size]);

  const handleOkClick = useCallback(() => {
    onSelect?.(Array.from(newSelectedDealIds), Array.from(removedDealIds));
    onHide(true);
  }, [newSelectedDealIds, onHide, onSelect, removedDealIds]);

  const handleCancel = useCallback(() => onHide(false), [onHide]);

  const handleAddClick = useRecordLimitCheckWith(showCreateDealModal);

  return (
    <Modal
      className={styles.modal}
      okButtonProps={{ disabled: !hasChanges }}
      okText={
        okText ??
        intl.formatMessage(messages.okButton, {
          addDeals: assignedDeals.length === 0,
          multiselect,
        })
      }
      onCancel={handleCancel}
      onOk={handleOkClick}
      open
      title={
        <div className={styles.modalTitle}>
          <span>{title ?? intl.formatMessage(messages.header, { multiselect })}</span>
          {modalSubTitle.length > 0 && <span className={styles.titleSubText}>{modalSubTitle}</span>}
        </div>
      }
      width="clamp(500px, 50vw, 750px)"
      zIndex={zIndex}
    >
      <Associations<Deal>
        alreadyAssociatedMessage={intl.formatMessage(messages.alreadyAssociated)}
        assignedRecords={alreadyAssignedEntities}
        associateWith={associateWith}
        cantAssociateWithSelfMessage={intl.formatMessage(messages.cantAssociateWithSelf)}
        disableNonEditableEntities={disableNonEditableEntities}
        entities={sortedDeals}
        extras={
          allowAdd && canCreateDeals ? (
            <Button onClick={handleAddClick} type="link">
              {intl.formatMessage(messages.addNewDealButton)}
            </Button>
          ) : undefined
        }
        filtered={filtered}
        loading={loading}
        multiselect={multiselect}
        noDataImageSrc={noRecordsImageMap[EntityType.DEAL]}
        notEditableMessage={intl.formatMessage(messages.notEditableRowTooltip)}
        onChange={handleRowsChange}
        onChangeQuery={setQuery}
        onSearch={handleSearch}
        placeholder={intl.formatMessage(messages.placeholder)}
        query={query}
        suggestedEntities={suggestedDeals}
        total={total}
        value={selectedDealIds}
      />
      {createNewDealVisible && (
        <CreateDealModal
          fixedCompany={
            fixedCompany
              ? fixedCompany
              : fixedPerson
                ? ((fixedPerson.primaryAccount ?? fixedPerson.accounts[0]) as Company | undefined)
                : fixedCompany
          }
          fixedPerson={fixedPerson}
          okText={intl.formatMessage(messages.createOkButton)}
          onHide={handleHideCreateDealModal}
        />
      )}
    </Modal>
  );
};

const mapStateToProps = (state: RootState) => ({
  canCreateDeals: hasPermission(state)(PermissionName.DEAL_CREATE),
  deals: getDeals(state),
  filtered: getFilteredDealsCount(state),
  loading: isLoading(state),
  total: getTotalDeals(state),
});

const mapDispatchToProps = {
  onSearch: filterDeals.request,
};

export default connect(mapStateToProps, mapDispatchToProps)(DealAssociation);
